export type EngineStatus = 'live' | 'beta' | 'coming-soon';

export type EngineRecord = {
  slug: string;
  name: string;
  status: EngineStatus;
  oneLiner: string;
  liveDemo?: string;
  whatItDoes: string;        // 2-3 sentences
  whatItEats: string;        // 1 sentence input description
  whatItOutputs: string;     // 1 sentence output description
  costPerRun: string;        // ballpark $
  whoItsFor: string;         // 1 sentence
  exampleDeployments: string[];
  bodyMdSrc?: 'engines/nexlytics-scorer/docs/README.md'; // optional: for engines with canonical docs
};

export const ENGINES: EngineRecord[] = [
  {
    slug: 'nexlytics-scorer',
    name: 'Nexlytics',
    status: 'live',
    oneLiner: 'File parser + scorer + RAG-grounded auditor',
    liveDemo: 'https://nexlytics.onwardsanalytics.com.au',
    whatItDoes: 'Parses a structured file, scores it against a configurable issue taxonomy, and emits a buyer-ready audit. The Power BI .pbix variant is live; the engine generalises to any structured input + scoring rubric pair.',
    whatItEats: 'A structured file matching the configured input format (pbix / figma-export / dbt-manifest / sql-schema).',
    whatItOutputs: 'A scored audit: issue list, total score, optional PDF, optional HTML preview.',
    costPerRun: '~$0.02',
    whoItsFor: 'BI engineers, design teams, data platform teams — anyone inheriting structured artefacts and needing a credible second opinion.',
    exampleDeployments: ['Figma file auditor for design teams', 'dbt model auditor for analytics engineers', 'SQL schema auditor for data platform teams'],
    bodyMdSrc: 'engines/nexlytics-scorer/docs/README.md',
  },
  {
    slug: 'mockingbird',
    name: 'Mockingbird',
    status: 'beta',
    oneLiner: 'Competitor scrape → vision blueprint → brand swap → Meta upload',
    liveDemo: 'https://mockingbird-9v1.pages.dev',
    whatItDoes: 'Mines a competitor library on Meta, decomposes each ad into a vision blueprint, swaps your brand into the structure, and pushes finished variants back to Meta Ads Manager. Built on top of the Nexlytics-style RAG + image-gen pipeline.',
    whatItEats: 'A brand kit + a set of competitor ads (URLs or scraped library).',
    whatItOutputs: 'Brand-coherent ad variants ready for upload, plus the blueprint reasoning that backs each one.',
    costPerRun: '~$0.20–$0.50 per ad variant (vision + image-gen tokens dominate)',
    whoItsFor: 'Indie SaaS founders who need ad-creative volume without an agency.',
    exampleDeployments: ['LinkedIn-post pattern miner', 'Instagram-Reels pattern miner', 'Email subject-line miner'],
  },
  {
    slug: 'pulse-check',
    name: 'Pulse Check',
    status: 'live',
    oneLiner: 'Multi-tenant scorecard + cohort benchmark with n≥3 floor',
    liveDemo: 'https://pulsecheck.onwardsanalytics.com.au',
    whatItDoes: 'Runs a structured survey across a cohort, anonymises respondents, and emits tier-level dashboards with a cohort-floor of n≥3 to protect individual identities. Live with Australian universities.',
    whatItEats: 'Survey schema + respondent invitations.',
    whatItOutputs: 'Per-tenant dashboard + cohort benchmark + cross-cohort comparison.',
    costPerRun: '~$3/respondent (mostly Supabase + Resend invitations)',
    whoItsFor: 'Anyone running comparative scorecards across a defined cohort: industry associations, regulators, peer-benchmarking products.',
    exampleDeployments: ['Sector benchmarking for logistics firms', 'Compliance scorecards for accounting practices', 'Maturity benchmarking for dental clinics'],
  },
  {
    slug: 'glide',
    name: 'Glide',
    status: 'beta',
    oneLiner: 'Specialist-domain CV + multi-metric scoring',
    liveDemo: 'https://aqua-stride-ai-78867.fly.dev',
    whatItDoes: 'Specialist computer vision on side-on video. Originally built for swim form (MediaPipe 2D + AthletePose3D 3D-lift); the engine generalises to any specialist single-camera CV task.',
    whatItEats: 'A short video clip (5-30s, side-on camera).',
    whatItOutputs: 'A multi-metric scorecard with per-frame keypoints, summary visualisations, and grading.',
    costPerRun: '~$0.50–$2 per clip (CV inference dominates)',
    whoItsFor: 'Niche-domain performance coaches — sport, presenting, sales-call grading, anything where a side-on video is the meaningful unit.',
    exampleDeployments: ['Running form analyser', 'Golf swing analyser', 'Sales-call recording grader'],
  },
  {
    slug: 'mobipet',
    name: 'Mobipet',
    status: 'beta',
    oneLiner: 'Mobile workflow + SMS coordination + booking',
    whatItDoes: 'Two-sided mobile-service marketplace pattern. Customer requests come in via web; pros are routed and coordinated via SMS; payment + receipts are handled end-to-end on Stripe. Live as a mobile vet marketplace.',
    whatItEats: 'Customer request (location, service type, urgency).',
    whatItOutputs: 'Matched-and-dispatched job with end-to-end SMS coordination + Stripe payment receipt.',
    costPerRun: '~$0.05 per SMS + Stripe processing',
    whoItsFor: 'Anyone running a two-sided mobile-service marketplace: groomers, mechanics, tutors, mobile beauty.',
    exampleDeployments: ['Mobile vet marketplace', 'Mobile groomer marketplace', 'Mobile mechanic dispatch'],
  },
  {
    slug: 'ai-cto',
    name: 'AI-CTO / Clarity Advisor',
    status: 'live',
    oneLiner: 'RAG over customer corpus → structured advisory output',
    liveDemo: 'https://clarity.onwardsanalytics.com.au',
    whatItDoes: 'Loads a customer corpus (books, transcripts, frameworks) into Vectorize, then answers questions with grounded reasoning against that corpus. Dual-use: powers Clarity itself, and is rentable as the engine for customers building Delphi/Coachvox-style advisor products.',
    whatItEats: 'A corpus (markdown / PDFs / transcripts), plus a user question.',
    whatItOutputs: 'A structured advisory response with citations to the corpus, plus persistent chat memory per tenant.',
    costPerRun: '~$0.01–$0.10 per question (Anthropic tokens dominate)',
    whoItsFor: 'Coaches, consultants, advisors with a body of work who want to scale their thinking as a product.',
    exampleDeployments: ['Branded AI sales coach', '"Ask the founder" advisor product', 'Niche legal advisor over a firm\'s archive'],
  },
];
